import { useState, type KeyboardEvent } from "react";
import type { Control } from "react-hook-form";
import { ChevronUp, Minus, Plus } from "lucide-react";
import type { MenuCardLabels } from "@shared/productNames";
import type { CustomerOrderInput, Product } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UnitPill } from "@/components/unit-pill";

type MenuProduct = Omit<Product, "imageData">;

// Units priced per unit of weight: the price reads "$1.00 / lb".
const PER_WEIGHT_UNITS = new Set(["lb", "kg"]);

const STEP_BUTTON =
  "bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg h-10 font-medium";

/**
 * One item sold by several units (case, half case, each), as one card on the customer
 * menu: the photo and the item once ("Lemons"), then a row per unit with only its tag,
 * price and quantity. Each row is its own product and its own cart entry (`quantities.product_<id>`),
 * exactly as if it had a card to itself — so every size's quantity is always on screen.
 * The card's one Add button drops the rows down, each already a − 0 + stepper. No
 * "Prev" shortcut here (Jon, 2026-10-13): that stays on single-product cards.
 * `labels` come from the page (`menuCardLabels`): a row that still has an old pack
 * shows the pack as its tag instead of the unit.
 * The quantity-typing state lives in the page, shared with the single-product cards.
 */
export function SizeGroupCard({
  head,
  members,
  labels,
  imageSrc,
  control,
  quantities,
  editingQuantity,
  editQuantityValue,
  onEditQuantityValue,
  onQuantityClick,
  onQuantitySave,
  onQuantityKeyDown,
}: {
  head: MenuProduct;
  members: MenuProduct[];
  labels: MenuCardLabels;
  imageSrc: string;
  control: Control<CustomerOrderInput>;
  quantities: Record<string, number>;
  editingQuantity: number | null;
  editQuantityValue: string;
  onEditQuantityValue: (value: string) => void;
  onQuantityClick: (currentValue: number, productId: number) => void;
  onQuantitySave: (field: any) => void;
  onQuantityKeyDown: (e: KeyboardEvent, field: any) => void;
}) {
  const [open, setOpen] = useState(false);
  // A size already in the cart keeps the card open, so its quantity is never hidden.
  const hasQuantity = members.some((m) => (quantities[`product_${m.id}`] || 0) > 0);
  const expanded = open || hasQuantity;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mobile-padding" data-testid={`card-size-group-${head.id}`}>
      {/* Collapsed, this reads like any single-product card: one Add button that drops the sizes down. */}
      <div className="flex gap-6">
        <div className="flex-shrink-0">
          <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
            <img src={imageSrc} alt={labels.title} className="w-full h-full object-cover rounded-lg" loading="lazy" decoding="async" />
          </div>
        </div>
        <div className="flex-1 flex flex-col justify-between min-h-[96px] sm:min-h-[112px]">
          <div className="mb-2">
            <h3 className="font-medium text-gray-900 text-sm sm:text-base mb-0.5" data-testid={`text-size-group-title-${head.id}`}>
              {labels.title}
            </h3>
            {head.subheading && <p className="text-xs sm:text-sm text-gray-600 font-light">{head.subheading}</p>}
          </div>
          {!hasQuantity && (
            <Button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={expanded}
              className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-4 w-full h-11 font-medium"
              data-testid={`button-sizes-toggle-${head.id}`}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : "Add"}
            </Button>
          )}
        </div>
      </div>

      {/* A table, so every row's unit and price cell is as wide as the card's widest one:
          the prices share one column, lined up on the decimal point. */}
      {expanded && (
      <div className="mt-3 table w-full border-collapse border-t border-gray-100">
        {members.map((member, index) => {
          // The row's tag (its unit, or an old pack in its place); with neither, its whole name.
          const tag = labels.tags[index];
          const safeKey = `product_${member.id}` as const;
          const currentQuantity = quantities[safeKey] || 0;
          return (
            <FormField
              key={member.id}
              control={control}
              name={`quantities.${safeKey}`}
              render={({ field }) => (
                <div
                  className="table-row border-b border-gray-100 last:border-b-0 [&>*]:py-2 [&>*]:align-middle [&:last-child>*]:pb-0"
                  data-testid={`row-size-${member.id}`}
                >
                  <div className="table-cell w-px whitespace-nowrap pr-2">
                    {tag ? (
                      // A long one (a hand-written pack) may wrap.
                      <UnitPill unit={tag} className={tag.length > 14 ? "ml-0 min-w-24 whitespace-normal leading-tight" : "ml-0"} />
                    ) : (
                      <span className="block min-w-24 whitespace-normal text-sm text-gray-700" data-testid={`text-size-label-${member.id}`}>
                        {member.name}
                      </span>
                    )}
                  </div>
                  <div className="table-cell w-px whitespace-nowrap text-right text-sm font-medium tabular-nums text-gray-700">
                    {member.price && `$${member.price}`}
                  </div>
                  <div className="table-cell whitespace-nowrap pl-1 pr-2 text-sm font-medium text-gray-700">
                    {member.price && member.unit && PER_WEIGHT_UNITS.has(member.unit) ? `/ ${member.unit}` : ""}
                  </div>

                  <div className="table-cell w-40 sm:w-48">
                    <div className="flex items-center gap-1.5">
                    {/* Open, a row is its stepper straight away, from 0: the card's own Add
                        was the tap that asked for it, so a second Add per row is one too many. */}
                    <Button
                      type="button"
                      onClick={() => field.onChange(Math.max(0, currentQuantity - 1))}
                      disabled={currentQuantity === 0}
                      className={`${STEP_BUTTON} w-11 px-0`}
                      aria-label="One fewer"
                      data-testid={`button-minus-${member.id}`}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    {editingQuantity === member.id ? (
                      <Input
                        type="number"
                        value={editQuantityValue}
                        onChange={(e) => onEditQuantityValue(e.target.value)}
                        onBlur={() => onQuantitySave(field)}
                        onKeyDown={(e) => onQuantityKeyDown(e, field)}
                        className="font-medium text-lg text-center h-10 px-1 flex-1 min-w-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                        autoFocus
                        data-testid={`input-quantity-${member.id}`}
                      />
                    ) : (
                      <div
                        className="flex-1 flex items-center justify-center h-10 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => onQuantityClick(currentQuantity, member.id)}
                        data-testid={`text-quantity-${member.id}`}
                      >
                        <span className={`font-medium text-lg ${currentQuantity === 0 ? "text-gray-400" : ""}`}>{currentQuantity}</span>
                      </div>
                    )}
                    <Button
                      type="button"
                      onClick={() => field.onChange(currentQuantity + 1)}
                      className={`${STEP_BUTTON} w-11 px-0`}
                      aria-label="One more"
                      data-testid={`button-plus-${member.id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    </div>
                  </div>
                </div>
              )}
            />
          );
        })}
      </div>
      )}
    </div>
  );
}
